import { useCallback, useEffect, useMemo, useState } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import {
    canUseVoice as defaultCanUseVoice,
    getPermissionState as defaultGetPermissionState,
    requestPermission as defaultRequestPermissionNative,
    openAppSettings as defaultOpenAppSettingsNative,
    type VoicePermissionState,
} from '../../native/voice-native';

const OPT_IN_KEY = 'voice.opt_in';

export interface UseVoiceOptInState {
    supported: boolean;
    optIn: boolean;
    permission: VoicePermissionState;
    loading: boolean;
    setOptIn: (next: boolean) => Promise<void>;
    requestPermission: () => Promise<VoicePermissionState>;
    openAppSettings: () => Promise<void>;
    refresh: () => Promise<void>;
}

export interface UseVoiceOptInDeps {
    canUseVoice?: () => boolean;
    getPermissionState?: () => Promise<VoicePermissionState>;
    requestPermissionNative?: () => Promise<VoicePermissionState>;
    openAppSettingsNative?: () => Promise<void>;
    preferences?: {
        get: (k: { key: string }) => Promise<{ value: string | null }>;
        set: (k: { key: string; value: string }) => Promise<void>;
    };
    appStateListener?: {
        addListener: (
            event: 'appStateChange',
            handler: (state: { isActive: boolean }) => void,
        ) => Promise<{ remove: () => void | Promise<void> }>;
    };
}

export function useVoiceOptIn(deps?: UseVoiceOptInDeps): UseVoiceOptInState {
    const resolved = useMemo(() => ({
        canUseVoice: deps?.canUseVoice ?? defaultCanUseVoice,
        getPermissionState: deps?.getPermissionState ?? defaultGetPermissionState,
        requestPermissionNative: deps?.requestPermissionNative ?? defaultRequestPermissionNative,
        openAppSettingsNative: deps?.openAppSettingsNative ?? defaultOpenAppSettingsNative,
        preferences: deps?.preferences ?? Preferences,
        appStateListener: deps?.appStateListener ?? CapApp,
    }), [deps]);

    const supported = resolved.canUseVoice();
    const [optIn, setOptInState] = useState(false);
    const [permission, setPermission] = useState<VoicePermissionState>('prompt');
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!supported) {
            setOptInState(false);
            setPermission('denied');
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const [{ value }, perm] = await Promise.all([
                resolved.preferences.get({ key: OPT_IN_KEY }),
                resolved.getPermissionState(),
            ]);
            setOptInState(value === 'true');
            setPermission(perm);
        } finally {
            setLoading(false);
        }
    }, [resolved, supported]);

    const setOptIn = useCallback(async (next: boolean) => {
        if (!supported) return;
        await resolved.preferences.set({ key: OPT_IN_KEY, value: next ? 'true' : 'false' });
        setOptInState(next);
    }, [resolved, supported]);

    const requestPermission = useCallback(async () => {
        if (!supported) return 'denied' as VoicePermissionState;
        const next = await resolved.requestPermissionNative();
        setPermission(next);
        return next;
    }, [resolved, supported]);

    const openAppSettings = useCallback(async () => {
        if (!supported) return;
        await resolved.openAppSettingsNative();
    }, [resolved, supported]);

    useEffect(() => {
        if (!supported) {
            setOptInState(false);
            setPermission('denied');
            setLoading(false);
            return;
        }

        let disposed = false;
        let removeAppState: (() => void) | null = null;

        void refresh();

        void resolved.appStateListener.addListener('appStateChange', (state) => {
            if (disposed) return;
            if (state.isActive) void refresh();
        }).then((handle) => {
            removeAppState = () => { void handle.remove(); };
            if (disposed) removeAppState();
        });

        return () => {
            disposed = true;
            removeAppState?.();
        };
    }, [resolved, refresh, supported]);

    return {
        supported,
        optIn,
        permission,
        loading,
        setOptIn,
        requestPermission,
        openAppSettings,
        refresh,
    };
}
